import { useEffect, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { CheckCircle2, Package, Truck } from "lucide-react";
import api, { inr } from "@/lib/api";

export default function OrderConfirmation() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const [order, setOrder] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    const token = params.get("t");
    api.get(`/orders/${id}${token ? `?token=${token}` : ""}`)
      .then(({ data }) => setOrder(data))
      .catch(() => setError(true));
    document.title = "Order Confirmed — KKashigari India";
  }, [id]);

  if (error) return (
    <div className="py-40 text-center px-5">
      <p className="font-serif text-3xl text-[var(--bronze)]">Order not found</p>
      <p className="text-[var(--olive)] mt-3">This order link is invalid or you don't have access to it.</p>
      <Link to="/account" className="btn-primary inline-block px-8 py-4 uplabel mt-6">View My Orders</Link>
    </div>
  );
  if (!order) return <div className="py-40 text-center uplabel text-[var(--olive)]">Loading…</div>;

  const isPaid = order.payment_status === "paid";
  const isCod = order.payment_method === "cod";

  return (
    <div data-testid="order-confirmation-page" className="max-w-2xl mx-auto px-5 py-16 lg:py-24">
      <div className="text-center">
        <CheckCircle2 className="w-16 h-16 text-[var(--emerald)] mx-auto" strokeWidth={1.2} />
        <div className="uplabel mt-6">Thank you for your order</div>
        <h1 className="font-serif text-4xl sm:text-5xl text-[var(--bronze)] mt-3">Order Confirmed</h1>
        <p className="text-[var(--olive)] mt-4">
          Order <span className="text-[var(--bronze)] font-medium" data-testid="order-number">{order.order_number}</span> ·
          A confirmation has been sent to {order.email}
        </p>
      </div>

      <div className="bg-[#F3EECB] p-8 mt-12">
        <div className="flex items-center justify-between pb-6 border-b border-[var(--line)]">
          <div>
            <div className="uplabel text-[0.62rem]">Payment</div>
            <div className="font-serif text-lg text-[var(--bronze)] mt-1">{isCod ? "Cash on Delivery" : "Paid Online"}</div>
          </div>
          <span data-testid="payment-status" className={`uplabel px-3 py-1.5 ${isPaid ? "bg-[var(--emerald)] text-[#FBF7DE]" : "bg-[var(--olive)] text-[#FBF7DE]"}`}>
            {isPaid ? "Paid" : isCod ? "COD — Pay on delivery" : "Pending"}
          </span>
        </div>

        <div className="py-6 space-y-4 border-b border-[var(--line)]">
          {order.items.map((it, i) => (
            <div key={i} className="flex gap-4 text-sm">
              <img src={it.image} alt="" className="w-14 aspect-[3/4] object-cover" />
              <div className="flex-1"><div className="text-[var(--bronze)]">{it.name}</div><div className="text-xs text-[var(--olive)]">Size {it.size} × {it.quantity}</div></div>
              <span className="text-[var(--bronze)]">{inr(it.price * it.quantity)}</span>
            </div>
          ))}
        </div>

        <div className="py-6 space-y-2 text-sm border-b border-[var(--line)]">
          <div className="flex justify-between text-[var(--bronze)]"><span>Subtotal</span><span>{inr(order.subtotal)}</span></div>
          {order.discount > 0 && <div className="flex justify-between text-[var(--emerald)]"><span>Discount</span><span>-{inr(order.discount)}</span></div>}
          <div className="flex justify-between text-[var(--bronze)]"><span>Shipping</span><span>{order.shipping === 0 ? "Free" : inr(order.shipping)}</span></div>
          {order.cod_fee > 0 && <div className="flex justify-between text-[var(--bronze)]"><span>COD Fee</span><span>{inr(order.cod_fee)}</span></div>}
        </div>
        <div className="flex justify-between font-serif text-xl text-[var(--bronze)] pt-6"><span>Total</span><span>{inr(order.total)}</span></div>
      </div>

      <div className="mt-8 flex items-center justify-center gap-8">
        <div className="flex items-center gap-2 text-sm text-[var(--olive)]"><Package className="w-4 h-4" /> Processing</div>
        <div className="w-10 h-px bg-[var(--line)]" />
        <div className="flex items-center gap-2 text-sm text-[var(--olive)]"><Truck className="w-4 h-4" /> On its way soon</div>
      </div>

      <div className="text-center mt-12">
        <Link to="/shop" className="btn-primary inline-block px-8 py-4 uplabel">Continue Shopping</Link>
      </div>
    </div>
  );
}
